/**
 * ContactRolePicker
 *
 * Dual-slot contact picker used in deal/job creation wizards. Resolves the
 * customer's primary account and site contacts as defaults, and lets the user
 * override either slot from the full contact list.
 *
 * When an addressId is supplied, the site contact defaults to any contact
 * explicitly linked to that address (via Contact.addressIds) before falling
 * back to the customer's general primary site contact.
 */

import { useEffect, useMemo, useState } from 'react';
import { Briefcase, MapPin, UserPlus, Link2 } from 'lucide-react';
import { Contact } from '@/types';
import { PhoneNumber } from './PhoneNumber';
import { getCustomerContacts, contactHasRole } from '@/services/contact.service';

export interface ContactRoleSelection {
  accountContactId?: string;
  accountContactName?: string;
  accountContactPhone?: string;
  accountContactEmail?: string;
  siteContactId?: string;
  siteContactName?: string;
  siteContactPhone?: string;
  siteContactEmail?: string;
}

interface Props {
  customerId: string | null;
  addressId?: string;           // optional — scopes site contact resolution to a specific address
  value: ContactRoleSelection;
  onChange: (next: ContactRoleSelection) => void;
  onAddContact?: () => void;    // optional — render "Add contact" button when supplied
  compact?: boolean;            // tighter layout for the job modal
}

function toSelection(c: Contact | null | undefined, role: 'account' | 'site'): Partial<ContactRoleSelection> {
  if (!c || !c.id) {
    return role === 'account'
      ? {
          accountContactId: undefined,
          accountContactName: undefined,
          accountContactPhone: undefined,
          accountContactEmail: undefined,
        }
      : {
          siteContactId: undefined,
          siteContactName: undefined,
          siteContactPhone: undefined,
          siteContactEmail: undefined,
        };
  }

  if (role === 'account') {
    return {
      accountContactId: c.id,
      accountContactName: c.name,
      accountContactPhone: c.phone,
      accountContactEmail: c.email,
    };
  }

  return {
    siteContactId: c.id,
    siteContactName: c.name,
    siteContactPhone: c.phone,
    siteContactEmail: c.email,
  };
}

/**
 * Pick the best primary for a role using the same precedence as contact.service.ts.
 * Duplicated client-side so this component can resolve defaults from an already-loaded
 * contacts array without a second network fetch.
 */
function resolvePrimary(
  contacts: Contact[],
  role: 'account' | 'site',
  addressId?: string,
): Contact | null {
  if (contacts.length === 0) return null;

  if (role === 'site' && addressId) {
    const addressSpecific = contacts.find(
      (c) => c.addressIds?.includes(addressId) && contactHasRole(c, 'site'),
    );
    if (addressSpecific) return addressSpecific;
  }

  if (role === 'account') {
    const byFlag = contacts.find((c) => c.isPrimaryAccount);
    if (byFlag) return byFlag;
  } else {
    const byFlag = contacts.find(
      (c) => c.isPrimarySite && (!c.addressIds || c.addressIds.length === 0),
    );
    if (byFlag) return byFlag;
  }

  const byLegacy = contacts.find((c) => c.isPrimary && contactHasRole(c, role));
  if (byLegacy) return byLegacy;

  return contacts.find((c) => contactHasRole(c, role)) || null;
}

export function ContactRolePicker({ customerId, addressId, value, onChange, onAddContact, compact }: Props) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastCustomerId, setLastCustomerId] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setContacts([]);
      return;
    }
    setLoading(true);
    getCustomerContacts(customerId)
      .then(setContacts)
      .catch((e) => {
        console.error('Failed to load contacts for picker:', e);
        setContacts([]);
      })
      .finally(() => setLoading(false));
  }, [customerId]);

  // Auto-populate defaults when customer changes. Only overwrite user-selected values
  // if the customer itself changed — avoids stomping manual overrides on an address-change.
  useEffect(() => {
    if (!customerId) return;
    if (contacts.length === 0) return;

    if (lastCustomerId !== customerId) {
      const account = resolvePrimary(contacts, 'account');
      const site = resolvePrimary(contacts, 'site', addressId);
      onChange({
        ...toSelection(account, 'account'),
        ...toSelection(site, 'site'),
      });
      setLastCustomerId(customerId);
      return;
    }

    // Same customer, address changed — only update site contact if user hasn't picked one manually
    // or if their previous pick was tied to the previous address.
    if (addressId) {
      const addressSpecific = contacts.find(
        (c) => c.addressIds?.includes(addressId) && contactHasRole(c, 'site'),
      );
      if (addressSpecific && addressSpecific.id !== value.siteContactId) {
        onChange({ ...value, ...toSelection(addressSpecific, 'site') });
      }
    }
     
  }, [contacts, customerId, addressId]);

  const accountCandidates = useMemo(
    () => contacts.filter((c) => contactHasRole(c, 'account')),
    [contacts],
  );
  const siteCandidates = useMemo(
    () => contacts.filter((c) => contactHasRole(c, 'site')),
    [contacts],
  );

  const sameAsAccount =
    !!value.accountContactId && value.accountContactId === value.siteContactId;

  const handleSelectAccount = (id: string) => {
    const c = contacts.find((x) => x.id === id) || null;
    onChange({ ...value, ...toSelection(c, 'account') });
  };

  const handleSelectSite = (id: string) => {
    if (id === '__same__') {
      // Copy the account contact into the site slot
      if (value.accountContactId) {
        onChange({
          ...value,
          siteContactId: value.accountContactId,
          siteContactName: value.accountContactName,
          siteContactPhone: value.accountContactPhone,
          siteContactEmail: value.accountContactEmail,
        });
      }
      return;
    }
    const c = contacts.find((x) => x.id === id) || null;
    onChange({ ...value, ...toSelection(c, 'site') });
  };

  if (!customerId) {
    return (
      <div className={`rounded-xl border border-theme bg-surface-input px-4 py-3 text-sm text-theme-muted ${compact ? '' : 'text-center'}`}>
        Select a customer first to pick contacts.
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* Account Contact */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-theme-secondary">
            <Briefcase className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
            Account Contact
            <span className="text-theme-muted font-normal">· billing &amp; decisions</span>
          </label>
          {onAddContact && (
            <button
              type="button"
              onClick={onAddContact}
              className="flex items-center gap-1 text-[11px] text-brand-secondary hover:brightness-125"
            >
              <UserPlus className="w-3 h-3" />
              Add contact
            </button>
          )}
        </div>
        <select
          value={value.accountContactId || ''}
          onChange={(e) => handleSelectAccount(e.target.value)}
          disabled={loading || accountCandidates.length === 0}
          className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 transition-colors disabled:opacity-50"
        >
          <option value="">
            {loading
              ? 'Loading contacts…'
              : accountCandidates.length === 0
                ? 'No account contacts — add one'
                : 'Select account contact…'}
          </option>
          {accountCandidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.isPrimaryAccount || (c.isPrimary && !c.isPrimarySite) ? ' · Primary' : ''}
              {c.role ? ` · ${c.role}` : ''}
            </option>
          ))}
        </select>
        {value.accountContactPhone && (
          <div className="text-[11px] text-theme-muted inline-flex items-center gap-1.5 flex-wrap">
            <PhoneNumber phone={value.accountContactPhone} hideCopyUntilHover />
            {value.accountContactEmail && <span>· {value.accountContactEmail}</span>}
          </div>
        )}
      </div>

      {/* Site Contact */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-theme-secondary">
          <MapPin className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
          Site Contact
          <span className="text-theme-muted font-normal">· on-site / day-to-day</span>
          {sameAsAccount && (
            <span className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 bg-brand-secondary/15 text-brand-secondary rounded text-[9px] font-bold uppercase">
              <Link2 className="w-2.5 h-2.5" /> same as account
            </span>
          )}
        </label>
        <select
          value={value.siteContactId || ''}
          onChange={(e) => handleSelectSite(e.target.value)}
          disabled={loading || siteCandidates.length === 0}
          className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-colors disabled:opacity-50"
        >
          <option value="">
            {loading
              ? 'Loading contacts…'
              : siteCandidates.length === 0
                ? 'No site contacts — add one'
                : 'Select site contact…'}
          </option>
          {value.accountContactId && value.accountContactId !== value.siteContactId && (
            <option value="__same__">— Same as account contact —</option>
          )}
          {siteCandidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.isPrimarySite || (c.isPrimary && !c.isPrimaryAccount) ? ' · Primary' : ''}
              {c.role ? ` · ${c.role}` : ''}
              {addressId && c.addressIds?.includes(addressId) ? ' · This site' : ''}
            </option>
          ))}
        </select>
        {value.siteContactPhone && (
          <div className="text-[11px] text-theme-muted inline-flex items-center gap-1.5 flex-wrap">
            <PhoneNumber phone={value.siteContactPhone} hideCopyUntilHover />
            {value.siteContactEmail && <span>· {value.siteContactEmail}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
